import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, CloudSnow, CloudLightning, Wind, Loader } from 'lucide-react';

interface WeatherData {
  temp: number;
  description: string;
  weathercode: number;
  humidity: number;
  windSpeed: number;
}

const getWeatherDescription = (code: number): string => {
  switch (code) {
    case 0: return "Despejado";
    case 1: case 2: case 3: return "Parcialmente nublado";
    case 45: case 48: return "Niebla";
    case 51: case 53: case 55: return "Llovizna";
    case 61: case 63: case 65: return "Lluvia";
    case 71: case 73: case 75: return "Nieve";
    case 77: return "Aguanieve";
    case 80: case 81: case 82: return "Lluvia fuerte";
    case 85: case 86: return "Nieve fuerte";
    case 95: return "Tormenta";
    case 96: case 99: return "Tormenta con granizo";
    default: return "Despejado";
  }
};

const getWeatherIcon = (code: number) => {
  if (code === 0) return <Sun className="h-6 w-6 text-yellow-500" />;
  if (code <= 3) return <Cloud className="h-6 w-6 text-gray-500" />;
  if (code <= 48) return <Wind className="h-6 w-6 text-gray-400" />;
  if (code <= 55) return <CloudRain className="h-6 w-6 text-blue-300" />;
  if (code <= 65) return <CloudRain className="h-6 w-6 text-blue-500" />;
  if (code <= 77) return <CloudSnow className="h-6 w-6 text-blue-300" />;
  if (code <= 82) return <CloudRain className="h-6 w-6 text-blue-600" />;
  if (code <= 86) return <CloudSnow className="h-6 w-6 text-blue-400" />;
  return <CloudLightning className="h-6 w-6 text-yellow-600" />;
};

export default function WeatherWidget() {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Coordenadas de Puerto de Sagunto
        const lat = 39.6833;
        const lon = -0.2167;
        
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&timezone=Europe/Madrid`
        );
        
        if (!response.ok) {
          throw new Error('Error al obtener el clima');
        }

        const data = await response.json();
        const current = data.current;

        setWeather({
          temp: Math.round(current.temperature_2m),
          description: getWeatherDescription(current.weather_code),
          weathercode: current.weather_code,
          humidity: current.relative_humidity_2m,
          windSpeed: Math.round(current.wind_speed_10m * 3.6) // m/s a km/h
        });
      } catch (err) {
        console.error('Error fetching weather:', err);
        setError('Error al cargar el clima');
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
    // Actualizar cada 30 minutos
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-gray-600">
        <Loader className="h-5 w-5 animate-spin" />
        <span className="text-sm">Cargando clima...</span>
      </div>
    );
  }

  if (error || !weather) {
    return null;
  }

  return (
    <div className="flex items-center space-x-4 px-4 py-2 bg-white rounded-lg shadow-sm">
      <div className="flex items-center">
        {getWeatherIcon(weather.weathercode)}
        <span className="ml-2 text-2xl font-semibold">{weather.temp}°C</span>
      </div>
      <div className="text-sm text-gray-600">
        <p className="capitalize">{weather.description}</p>
        <p className="text-xs">Puerto de Sagunto</p>
      </div>
      <div className="hidden md:block text-xs text-gray-500">
        <p>Humedad: {weather.humidity}%</p>
        <p>Viento: {weather.windSpeed} km/h</p>
      </div>
    </div>
  );
}